import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import OwnerSidebar from '../../../Components/OwnerSidebar/OwnerSidebar';
import ownerAPI from '../../../services/ownerApi';
import './ClubManagementPage.css';

const ClubManagementPage = () => {
  const navigate = useNavigate();
  const { owner, isAuthenticated } = useSelector((state) => state.ownerAuth);
  const [clubs, setClubs] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [deletingClubId, setDeletingClubId] = useState(null);

  // 권한 체크
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/owner/login');
      return;
    }
    
    if (owner && !owner.is_superuser) {
      navigate('/owner/dashboard');
      return;
    }
  }, [isAuthenticated, owner, navigate]);

  // 데이터 로딩
  useEffect(() => {
    if (owner?.is_superuser) {
      Promise.all([fetchClubs(), fetchOwners()]);
    }
  }, [owner]);

  const fetchClubs = async () => {
    try {
      const clubs = await ownerAPI.getClubs();
      setClubs(clubs);
    } catch (err) {
      console.error('Failed to fetch clubs:', err);
      setError('클럽 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const fetchOwners = async () => {
    try {
      // 오너 목록을 가져오는 API가 필요함 (추후 구현)
      // const owners = await ownerAPI.getOwners();
      // setOwners(owners);
      console.log('오너 목록 조회 기능은 추후 구현 예정');
    } catch (err) {
      console.error('Failed to fetch owners:', err);
    }
  };

  const handleDelete = async (clubId, clubName) => {
    if (!window.confirm(`정말로 "${clubName}" 클럽을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) {
      return;
    }

    setDeletingClubId(clubId);
    try {
      await ownerAPI.deleteClub(clubId);
      alert('클럽이 성공적으로 삭제되었습니다.');
      fetchClubs();
    } catch (err) {
      console.error('Failed to delete club:', err);
      alert('클럽 삭제에 실패했습니다: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingClubId(null);
    }
  };

  const handleEdit = (clubId) => {
    navigate(`/owner/edit?clubId=${clubId}`);
  };

  const handleAssignOwner = (club) => {
    setSelectedClub(club);
    setShowAssignModal(true);
  };

  const openAddModal = () => {
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedClub(null);
  };

  if (!owner?.is_superuser) {
    return null; // 권한이 없으면 아무것도 렌더링하지 않음
  }

  return (
    <div className="club-management-wrapper">
      <OwnerSidebar />
      <main className={`club-management-main`}>
        <div className="club-management-header">
          <h1>클럽 관리</h1>
          <button className="add-club-btn" onClick={openAddModal}>
            새 클럽 추가
          </button>
        </div>

        {error && <p className="error-text">{error}</p>}

        <div className="club-list-container">
          {loading ? (
            <p>로딩 중...</p>
          ) : (
            <div className="club-grid">
              {clubs.map((club) => (
                <div key={club.id} className="club-card">
                  <div className="club-image">
                    {club.images && club.images[0] ? (
                      <img src={club.images[0].image_url} alt={club.name} />
                    ) : (
                      <div className="no-image">이미지 없음</div>
                    )}
                  </div>
                  <div className="club-info">
                    <h3>{club.name}</h3>
                    {club.korean_name && <p className="club-korean-name">{club.korean_name}</p>}
                    <p className="club-location">{club.location}</p>
                    <p className="club-address">{club.address || '주소 정보 없음'}</p>
                    <div className="club-owner-info">
                      <span className="owner-label">소유자:</span>
                      <span className="owner-name">
                        {club.owner ? club.owner.name : '미할당'}
                      </span>
                      <button 
                        className="assign-btn"
                        onClick={() => handleAssignOwner(club)}
                        title="소유자 할당/변경"
                      >
                        할당
                      </button>
                    </div>
                  </div>
                  <div className="club-actions">
                    <button className="edit-btn" onClick={() => handleEdit(club.id)}>
                      수정
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDelete(club.id, club.name)}
                      disabled={deletingClubId === club.id}
                    >
                      {deletingClubId === club.id ? '삭제 중...' : '삭제'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {clubs.length === 0 && !loading && (
          <div className="no-clubs">
            등록된 클럽이 없습니다.
          </div>
        )}

        {/* 클럽 추가 모달 */}
        {showAddModal && (
          <AddClubModal 
            onClose={closeAddModal}
            onSuccess={() => {
              closeAddModal();
              fetchClubs();
            }}
          />
        )}

        {/* 소유자 할당 모달 */}
        {showAssignModal && selectedClub && (
          <AssignOwnerModal
            club={selectedClub}
            owners={owners}
            onClose={closeAssignModal}
            onSuccess={() => {
              closeAssignModal();
              fetchClubs();
            }}
          />
        )}
      </main>
    </div>
  );
};

// 클럽 추가 모달 컴포넌트
const AddClubModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    korean_name: '',
    location: '',
    address: '',
    description: '',
    playlist: '',
    dj_name: '',
    genres: '',
    operating_hours: '',
    smoking_policy: false,
    instagram_url: '',
    ticket_limit_per_day: 100,
    ticket_validity_days: 7,
    ticket_entry_time: '',
  });
  const [priceMen, setPriceMen] = useState('');
  const [priceWomen, setPriceWomen] = useState('');
  const [newImages, setNewImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setNewImages(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('ownerToken');
      const form = new FormData();

      // 기본 필드들
      Object.keys(formData).forEach(key => {
        form.append(key, formData[key]);
      });

      // 가격 필드들
      if (priceMen) form.append('price_men', parseInt(priceMen));
      if (priceWomen) form.append('price_women', parseInt(priceWomen));

      // 이미지들
      newImages.forEach(file => {
        form.append('new_images', file);
      });

      await ownerAPI.createClub(form);

      alert('클럽이 성공적으로 추가되었습니다!');
      onSuccess();
    } catch (err) {
      console.error('Failed to create club:', err);
      alert('클럽 추가에 실패했습니다: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>새 클럽 추가</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="add-club-form">
          <div className="form-row">
            <div className="form-group">
              <label>클럽명 *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>한글명</label>
              <input
                type="text"
                name="korean_name"
                value={formData.korean_name}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>위치 *</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="강남, 이태원, 홍대 등"
                required
              />
            </div>
            <div className="form-group">
              <label>주소</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>소개</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>DJ 이름</label>
              <input
                type="text"
                name="dj_name"
                value={formData.dj_name}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>장르</label>
              <input
                type="text"
                name="genres"
                value={formData.genres}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>남성 입장료</label>
              <input
                type="number"
                value={priceMen}
                onChange={(e) => setPriceMen(e.target.value)}
                placeholder="원"
              />
            </div>
            <div className="form-group">
              <label>여성 입장료</label>
              <input
                type="number"
                value={priceWomen}
                onChange={(e) => setPriceWomen(e.target.value)}
                placeholder="원"
              />
            </div>
          </div>

          <div className="form-group">
            <label>운영시간</label>
            <input
              type="text"
              name="operating_hours"
              value={formData.operating_hours}
              onChange={handleChange}
              placeholder="예: 화-토 22:00-06:00"
            />
          </div>

          <div className="form-group">
            <label>플레이리스트 URL</label>
            <input
              type="url"
              name="playlist"
              value={formData.playlist}
              onChange={handleChange}
              placeholder="Spotify 플레이리스트 URL"
            />
          </div>

          <div className="form-group">
            <label>이미지</label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageChange}
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                name="smoking_policy"
                checked={formData.smoking_policy}
                onChange={handleChange}
              />
              흡연 허용
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose}>취소</button>
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? '추가 중...' : '클럽 추가'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// 소유자 할당 모달 컴포넌트
const AssignOwnerModal = ({ club, owners, onClose, onSuccess }) => {
  const [selectedOwnerId, setSelectedOwnerId] = useState(club.owner_id || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 소유자 할당 API 호출 (추후 구현 필요)
      // await ownerAPI.assignClubOwner(club.id, selectedOwnerId);
      console.log(`클럽 ${club.id}에 오너 ${selectedOwnerId} 할당 요청`);
      alert('소유자가 성공적으로 할당되었습니다.');
      onSuccess();
    } catch (err) {
      console.error('Failed to assign owner:', err);
      alert('소유자 할당에 실패했습니다: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>소유자 할당 - {club.name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="assign-form">
          <div className="form-group">
            <label>현재 소유자</label>
            <p className="current-owner">
              {club.owner ? club.owner.name : '미할당'}
            </p>
          </div>

          <div className="form-group">
            <label>새 소유자 선택</label>
            <select
              value={selectedOwnerId}
              onChange={(e) => setSelectedOwnerId(e.target.value)}
              required
            >
              <option value="">소유자를 선택하세요</option>
              {owners.map((owner) => (
                <option key={owner.id} value={owner.id}>
                  {owner.name} ({owner.email})
                </option>
              ))}
            </select>
            <p className="note">
              * 오너 목록 조회 기능은 현재 개발 중입니다.
            </p>
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              취소
            </button>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={isSubmitting || !selectedOwnerId}
            >
              {isSubmitting ? '할당 중...' : '할당하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClubManagementPage; 